<template>
  <section class="faq-section">
    <div class="faq-section__container">
      <h2 v-if="!hideTitle" class="faq-section__title">FAQ</h2>
      <div class="faq-list">
        <div v-for="(item, idx) in props.item?.fieldData" :key="idx" class="faq-item"
          :class="{ 'faq-item--open': openIndex === idx }">
          <button class="faq-item__header" @click="toggleItem(idx)" :aria-expanded="openIndex === idx">
            <span class="faq-item__question">{{ getCellTitle(item) }}</span>
            <span class="faq-item__icon" :class="{ 'faq-item__icon--open': openIndex === idx }">
              <ChevronRight v-if="openIndex !== idx" />
              <ChevronDown v-else />
            </span>
          </button>
          <Transition name="faq-collapse">
            <div v-show="openIndex === idx" class="faq-item__body">
              <p class="faq-item__answer">{{ getCellAnswer(item) }}</p>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import ChevronRight from '@/assets/icons/chevron-right.svg'
import ChevronDown from '@/assets/icons/chevron-down.svg'
import { Module } from '@/api/page_config'
import { getStrWithRecord } from '.'

const props = withDefaults(defineProps<{
  item?: Module
  hideTitle?: boolean
}>(), { hideTitle: false })

const openIndex = ref<number>(0)

function getCellTitle(cell: unknown): string {
  return getStrWithRecord(cell, 'title')
}
function getCellAnswer(cell: unknown): string {
  return getStrWithRecord(cell, 'content')
}

function toggleItem(idx: number): void {
  openIndex.value = openIndex.value === idx ? -1 : idx
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;

.faq-section {
  width: 100%;
}

.faq-section__container {
  max-width: $container-max-width;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: $breakpoint-md) {
    padding: 0 2rem;
  }
}

.faq-section__title {
  font-size: 2rem;
  font-weight: 800;
  color: $color-text;
  text-align: center;
  margin: 0 0 2rem;
  letter-spacing: -0.02em;

  @media (min-width: $breakpoint-md) {
    font-size: 2.75rem;
    margin-bottom: 3rem;
  }
}

.faq-list {
  display: flex;
  flex-direction: column;
}

.faq-item {
  border-bottom: 1px solid #e0e0e0;
  padding: 0;

  &--open {
    .faq-item__question {
      color: $orange;
    }
  }
}

.faq-item__header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: color 0.2s ease;

  &:hover .faq-item__question {
    color: $orange;
  }

  @media (min-width: $breakpoint-md) {
    padding: 1.5rem 0;
  }
}

.faq-item__question {
  font-size: 1.125rem;
  font-weight: 700;
  color: $color-text;
  line-height: 1.4;
  flex: 1;
  transition: color 0.2s ease;

  @media (min-width: $breakpoint-md) {
    font-size: 1.5rem;
  }
}

.faq-item__icon {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $color-text-secondary;
  transition: color 0.2s ease;

  svg {
    width: 1.25rem;
    height: 1.25rem;

    @media (min-width: $breakpoint-md) {
      width: 1.5rem;
      height: 1.5rem;
    }
  }

  &--open {
    color: $orange;
  }
}

.faq-item__body {
  padding-bottom: 1.25rem;
  overflow: hidden;

  @media (min-width: $breakpoint-md) {
    padding-bottom: 1.5rem;
  }
}

.faq-item__answer {
  font-size: 0.9375rem;
  color: $color-text-secondary;
  line-height: 1.7;
  margin: 0;
  padding-right: 2.5rem;

  @media (min-width: $breakpoint-md) {
    font-size: 1.125rem;
    padding-right: 3rem;
  }
}

/* Collapse transition */
.faq-collapse-enter-active {
  transition: all 0.3s ease;
  max-height: 500px;
  opacity: 1;
}

.faq-collapse-leave-active {
  transition: all 0.25s ease;
  max-height: 0;
  opacity: 0;
}

.faq-collapse-enter-from {
  max-height: 0;
  opacity: 0;
}

.faq-collapse-enter-to {
  max-height: 500px;
  opacity: 1;
}

.faq-collapse-leave-from {
  max-height: 500px;
  opacity: 1;
}

.faq-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
